import React, { useState, useEffect } from "react";
import { callAPI, useStore, useUser, usePage } from "../../Utils/utils";
import { vendor_page_labels } from "../../Constants/Labels";
import Layout from "../Global/Layout";
import AddBlockButton from "./AddBlockButton/AddBlockButton";
import BannerBlock from "./BannerBlock/BannerBlock";
import TextAndImageBlock from "./TextAndImageBlock/TextAndImageBlock";
import ImageAndTextBlock from "./ImageAndTextBlock/ImageAndTextBlock";
import TextAndVideoBlock from "./TextAndVideoBlock/TextAndVideoBlock";
import VideoAndTextBlock from "./VideoAndTextBlock/VideoAndTextBlock";
import ProcessBlock from "./ProcessBlock/ProcessBlock";
import ProductsBlock from "./FeaturedProductsBlock/FeaturedProductsBlock";
import ImageSelector from "../ImageSelector/ImageSelector";
import "./HomePage.css";

const HomePage = () => {
  const { user, setUser } = useUser();
  const { store, setStore } = useStore();
  const { page, setPage } = usePage();

  const [blocks, setBlocks] = useState([]);
  const [showBlockOptions, setShowBlockOptions] = useState(false);
  const [config, setConfig] = useState();
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [loading, setLoading] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Fetch page data on component mount
  useEffect(() => {
    const fetchPageData = async () => {
      const pay_load = {
        store_id: store?.id,
        page_id: 1,
      };

      try {
        setLoading(true);
        setBlocks([]); // Clear blocks before loading new data
        const response = await callAPI(
          "/admin/settings/pages/get",
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              token: user?.auth_token,
            },
            body: JSON.stringify(pay_load),
          },
          null, // navigate
          setLoading,
          setStore,
        );

        if (response && response.record && response.record.config?.blocks) {
          setBlocks(response.record.config.blocks);
        }
      } catch (error) {
        console.error("Error fetching page data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (store?.id && user?.auth_token) {
      fetchPageData();
    }
  }, [store?.id, user?.auth_token]);

  console.log("blocks", blocks);

  // Save page data to API
  const savePageData = async () => {
    const pay_load = {
      store_id: store?.id,
      page_id: 1,
      config: {
        blocks: blocks,
      },
    };

    try {
      setLoading(true);
      const response = await callAPI(
        "/admin/settings/pages/save",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            token: user?.auth_token,
          },
          body: JSON.stringify(pay_load),
        },
        null, // navigate
        setLoading,
        setStore,
      );

      if (response && response.success) {
        console.log("Page data saved successfully");
      }
    } catch (error) {
      console.error("Error saving page data:", error);
    } finally {
      setLoading(false);
    }
  };

  const addBlock = (blockType) => {
    let newBlock = {
      //   id: Date.now(),
      name: blockType,
      data: getDefaultBlockData(blockType),
    };

    // Initialize block-specific data if it's a banner block
    if (blockType === "vendor_banner") {
      newBlock = {
        label: "Banner",
        name: "vendor_banner",
        content: {
          left_content: {
            heading: "",
            sub_heading: "",
            description: "",
            button_label: "",
            button_url: "",
          },
          right_content: {
            img_button_url: "",
          },
        },
      };
    }

    // Initialize block-specific data if it's a text and image block
    if (blockType === "textAndImage") {
      newBlock = {
        label: "Text And Image",
        name: "vendor_text_and_image",
        content: {
          left_content: {
            heading: "",
            description: "",
            primary_button_label: "",
            primary_button_url: "",
            primary_is_pop_button: false,
            primary_pop_url: "",
            primary_pop_embed_url: "",
            secondary_button_label: "",
            secondary_button_url: "",
            secondary_is_pop_button: false,
            secondary_pop_url: "",
            secondary_pop_embed_url: "",
            popup_button_label: "",
            popup_video_url: "",
            popup_video_embed_url: "",
          },
          right_content: {},
        },
      };
    }

    // Initialize block-specific data if it's a text and video block
    if (blockType === "textAndVideo") {
      newBlock = {
        label: "Text And Video",
        name: "vendor_text_and_video",
        content: {
          left_content: {
            heading: "",
            description: "",
            primary_button_label: "",
            primary_button_url: "",
            secondary_button_label: "",
            secondary_button_url: "",
          },
          right_content: {
            video_url: "",
            embed_url: "",
          },
        },
      };
    }

    // Initialize block-specific data if it's a video and text block
    if (blockType === "videoAndText") {
      newBlock = {
        label: "Video And Text",
        name: "vendor_video_and_text",
        content: {
          left_content: {
            video_url: "",
            embed_url: "",
          },
          right_content: {
            heading: "",
            description: "",
            primary_button_label: "",
            primary_button_url: "",
            secondary_button_label: "",
            secondary_button_url: "",
          },
        },
      };
    }

    // Initialize block-specific data if it's an image and text block
    if (blockType === "imageAndText") {
      newBlock = {
        label: "Image And Text",
        name: "vendor_image_and_text",
        content: {
          right_content: {
            heading: "",
            description: "",
            primary_button_label: "",
            primary_button_url: "",
            secondary_button_label: "",
            secondary_button_url: "",
          },
          left_content: {},
        },
      };
    }

    // Initialize block-specific data if it's a process block
    if (blockType === "process") {
      newBlock = {
        label: "Process",
        name: "process",
        content: {
          sub_heading: "We Make Sending Greeting Cards Easy",
          heading: "Every Card Includes a Donation to the Nonprofit You Choose",
          contentWrapper: [
            {
              img_url: "/img/card-design.png",
              img_name: "card design",
              description: "Select a Card Design",
            },
            {
              img_url: "/img/write-message.png",
              img_name: "write a message",
              description: "Write a Personal Message",
            },
            {
              img_url: "/img/info.png",
              img_name: "recipients info",
              description: "Upload Your Recipients’ Info",
            },
            {
              img_url: "/img/donation.png",
              img_name: "donation amount",
              description: "Choose Your Donation Amount",
            },
            {
              img_url: "/img/print.png",
              img_name: "print",
              description: "We’ll Print and Mail Your Cards for You",
            },
          ],
        },
      };
    }

    // Initialize block-specific data if it's a featured products block
    if (blockType === "catalog") {
      newBlock = {
        label: "Catalog",
        name: "catalog",
      };
    }

    console.log("newBlock", newBlock);

    setBlocks([...blocks, newBlock]);
    setShowBlockOptions(false);
  };

  const getDefaultBlockData = (blockType) => {
    switch (blockType) {
      case "vendor_banner":
        return {
          label: "Banner",
          name: "vendor_banner",
          content: {
            left_content: {
              heading: "",
              sub_heading: "",
              description: "",
              button_label: "",
              button_url: "",
            },
            right_content: {
              img_button_url: "",
            },
          },
        };
      case "textAndImage":
        return {
          label: "Text And Image",
          name: "vendor_text_and_image",
          content: {
            left_content: {
              heading: "",
              description: "",
              primary_button_label: "",
              primary_button_url: "",
              primary_is_pop_button: false,
              primary_pop_url: "",
              primary_pop_embed_url: "",
              secondary_button_label: "",
              secondary_button_url: "",
              secondary_is_pop_button: false,
              secondary_pop_url: "",
              secondary_pop_embed_url: "",
              popup_button_label: "",
              popup_video_url: "",
              popup_video_embed_url: "",
            },
            right_content: {},
          },
        };
      case "textAndVideo":
        return {
          label: "Text And Video",
          name: "vendor_text_and_video",
          content: {
            left_content: {
              heading: "",
              description: "",
              primary_button_label: "",
              primary_button_url: "",
              secondary_button_label: "",
              secondary_button_url: "",
            },
            right_content: {
              video_url: "",
              embed_url: "",
            },
          },
        };
      case "videoAndText":
        return {
          label: "Video And Text",
          name: "vendor_video_and_text",
          content: {
            left_content: {
              video_url: "",
              embed_url: "",
            },
            right_content: {
              heading: "",
              description: "",
              primary_button_label: "",
              primary_button_url: "",
              secondary_button_label: "",
              secondary_button_url: "",
            },
          },
        };
      case "imageAndText":
        return {
          label: "Image And Text",
          name: "vendor_image_and_text",
          content: {
            right_content: {
              heading: "",
              description: "",
              primary_button_label: "",
              primary_button_url: "",
              secondary_button_label: "",
              secondary_button_url: "",
            },
            left_content: {},
          },
        };
      case "process":
        return {
          label: "Process",
          name: "process",
          content: {
            sub_heading: "We Make Sending Greeting Cards Easy",
            heading:
              "Every Card Includes a Donation to the Nonprofit You Choose",
            contentWrapper: [
              {
                img_url: "/img/card-design.png",
                img_name: "card design",
                description: "Select a Card Design",
              },
              {
                img_url: "/img/write-message.png",
                img_name: "write a message",
                description: "Write a Personal Message",
              },
              {
                img_url: "/img/info.png",
                img_name: "recipients info",
                description: "Upload Your Recipients’ Info",
              },
              {
                img_url: "/img/donation.png",
                img_name: "donation amount",
                description: "Choose Your Donation Amount",
              },
              {
                img_url: "/img/print.png",
                img_name: "print",
                description: "We’ll Print and Mail Your Cards for You",
              },
            ],
          },
        };
      case "catalog":
        return {
          label: "Catalog",
          name: "catalog",
        };
      default:
        return {};
    }
  };

  const removeBlock = (blockId) => {
    setBlocks(blocks.filter((block, index) => index !== blockId));
  };

  // Drag and drop handlers (from CategoryList)
  const handleDragStart = (e, index) => {
    const block = blocks[index];
    setDraggedItem(block);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      const newOrder = [...blocks];
      const [removed] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(dropIndex, 0, removed);

      setBlocks(newOrder);
      // Auto-save when order changes
      //   setTimeout(() => savePageData(), 500);
    }
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const updateBlock = (blockId, newData) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) =>
        index === blockId
          ? { ...block, data: { ...block.data, ...newData } }
          : block,
      ),
    );
  };

  // Banner block functions
  const onChangeBannerBlockField = (blockId, event, content) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) =>
        index === blockId
          ? {
            ...block,
            content: {
              ...block.content,
              [content]: {
                ...block.content?.[content],
                [event.target.name]: event.target.value,
              },
            },
          }
          : block,
      ),
    );
  };

  const onChangeBannerBlockDescription = (blockId, value) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) =>
        index === blockId
          ? {
            ...block,
            content: {
              ...block.content,
              left_content: {
                ...block.content?.left_content,
                description: value,
              },
            },
          }
          : block,
      ),
    );
  };

  const onOpenBannerImageBlockSelector = (blockId, config) => {
    setConfig({ ...config, bannerBlockImage: true, currentBlockId: blockId });
    setShowImageSelector(true);
  };

  const onRemoveBannerBlockImage = (blockId) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) =>
        index === blockId
          ? {
            ...block,
            content: {
              ...block.content,
              right_content: {
                ...block.content?.right_content,
                image_id: null,
                path: null,
                name: null,
              },
            },
          }
          : block,
      ),
    );
  };

  const onSelectImages = (images) => {
    if (config?.bannerBlockImage && config?.currentBlockId >= 0) {
      setBlocks((prevBlocks) =>
        prevBlocks.map((block, index) =>
          index === config.currentBlockId
            ? {
              ...block,
              content: {
                ...block.content,
                right_content: {
                  ...block.content?.right_content,
                  image_id: images?.image_id,
                  path: images?.path,
                  name: images?.name,
                },
              },
            }
            : block,
        ),
      );
    }
    if (config?.textAndImageBlockImage && config?.currentBlockId) {
      setBlocks((prevBlocks) =>
        prevBlocks.map((block, index) =>
          index === config.currentBlockId
            ? {
              ...block,
              content: {
                ...block.content,
                right_content: images,
              },
            }
            : block,
        ),
      );
    }
    if (config?.imageAndTextBlockImage && config?.currentBlockId) {
      setBlocks((prevBlocks) =>
        prevBlocks.map((block, index) =>
          index === config.currentBlockId
            ? {
              ...block,
              content: {
                ...block.content,
                left_content: images,
              },
            }
            : block,
        ),
      );
    }
    setShowImageSelector(false);
  };

  const onClose = () => {
    setShowImageSelector(false);
  };

  // Text and Image block functions
  const onChangeTextAndImageBlockField = (blockId, event, content) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) => {
        if (index === blockId) {
          let additionalFields = {};
          if (event.target.name === "primary_pop_url") {
            additionalFields.primary_pop_embed_url = getEmbedUrl(event.target.value);
          }
          if (event.target.name === "secondary_pop_url") {
            additionalFields.secondary_pop_embed_url = getEmbedUrl(event.target.value);
          }
          if (event.target.name === "popup_video_url") {
            additionalFields.popup_video_embed_url = getEmbedUrl(event.target.value);
          }
          return {
            ...block,
            content: {
              ...block.content,
              [content]: {
                ...block.content?.[content],
                [event.target.name]: event.target.value,
                ...additionalFields,
              },
            },
          };
        }
        return block;
      }),
    );
  };

  const onChangeTextAndImageBlockDescription = (blockId, value) => {
    console.log("id-val", blockId, value);

    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) =>
        index === blockId
          ? {
            ...block,
            content: {
              ...block.content,
              left_content: {
                ...block.content?.left_content,
                description: value,
              },
            },
          }
          : block,
      ),
    );
  };

  const onOpenTextAndImageBlockImageSelector = (blockId, config) => {
    setConfig({
      ...config,
      textAndImageBlockImage: true,
      currentBlockId: blockId,
    });
    setShowImageSelector(true);
  };

  const onRemoveTextAndImageBlockImage = (blockId) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) =>
        index === blockId
          ? {
            ...block,
            content: {
              ...block.content,
              right_content: null,
            },
          }
          : block,
      ),
    );
  };

  // Image and Text block functions
  const onChangeImageAndTextBlockField = (blockId, event) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) => {
        if (index === blockId) {
          let additionalFields = {};
          if (event.target.name === "primary_pop_url") {
            additionalFields.primary_pop_embed_url = getEmbedUrl(event.target.value);
          }
          if (event.target.name === "secondary_pop_url") {
            additionalFields.secondary_pop_embed_url = getEmbedUrl(event.target.value);
          }
          if (event.target.name === "popup_video_url") {
            additionalFields.popup_video_embed_url = getEmbedUrl(event.target.value);
          }
          return {
            ...block,
            content: {
              ...block.content,
              right_content: {
                ...block.content?.right_content,
                [event.target.name]: event.target.value,
                ...additionalFields,
              },
            },
          };
        }
        return block;
      }),
    );
  };

  const onChangeImageAndTextBlockDescription = (blockId, value) => {
    console.log("id-val", blockId, value);

    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) =>
        index === blockId
          ? {
            ...block,
            content: {
              ...block.content,
              right_content: {
                ...(block.content?.right_content || {}),
                description: value,
              },
            },
          }
          : block,
      ),
    );
  };

  const onOpenImageAndTextBlockImageSelector = (blockId, config) => {
    setConfig({
      ...config,
      imageAndTextBlockImage: true,
      currentBlockId: blockId,
    });
    setShowImageSelector(true);
  };

  const onRemoveImageAndTextBlockImage = (blockId) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) =>
        index === blockId
          ? {
            ...block,
            content: {
              ...block.content,
              left_content: null,
            },
          }
          : block,
      ),
    );
  };

  // Video helpers
  const getEmbedUrl = (url) => {
    if (!url) return "";
    let embedUrl = url;
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
    if (ytMatch && ytMatch[1]) {
      embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`;
    }
    const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vimeoMatch && vimeoMatch[1]) {
      embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    return embedUrl;
  };

  // Text and Video block functions
  const onChangeVendorTextAndVideoBlockField = (blockId, event, contentSection) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) => {
        if (index === blockId) {
          if (contentSection) {
            let additionalFields = {};
            if (event.target.name === "video_url") {
              additionalFields.embed_url = getEmbedUrl(event.target.value);
            }
            return {
              ...block,
              content: {
                ...block.content,
                [contentSection]: {
                  ...block.content?.[contentSection],
                  [event.target.name]: event.target.value,
                  ...additionalFields,
                },
              },
            };
          } else {
            return {
              ...block,
              content: {
                ...block.content,
                [event.target.name]: event.target.value,
              },
            };
          }
        }
        return block;
      })
    );
  };

  const onChangeVendorTextAndVideoBlockDescription = (blockId, value) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) =>
        index === blockId
          ? {
            ...block,
            content: {
              ...block.content,
              left_content: {
                ...block.content?.left_content,
                description: value,
              },
            },
          }
          : block,
      ),
    );
  };

  // Video and Text block functions
  const onChangeVendorVideoAndTextBlockField = (blockId, event, contentSection) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) => {
        if (index === blockId) {
          if (contentSection) {
            let additionalFields = {};
            if (event.target.name === "video_url") {
              additionalFields.embed_url = getEmbedUrl(event.target.value);
            }
            return {
              ...block,
              content: {
                ...block.content,
                [contentSection]: {
                  ...block.content?.[contentSection],
                  [event.target.name]: event.target.value,
                  ...additionalFields,
                },
              },
            };
          } else {
            return {
              ...block,
              content: {
                ...block.content,
                [event.target.name]: event.target.value,
              },
            };
          }
        }
        return block;
      })
    );
  };

  const onChangeVendorVideoAndTextBlockDescription = (blockId, value) => {
    setBlocks((prevBlocks) =>
      prevBlocks.map((block, index) =>
        index === blockId
          ? {
            ...block,
            content: {
              ...block.content,
              right_content: {
                ...block.content?.right_content,
                description: value,
              },
            },
          }
          : block,
      ),
    );
  };

  const renderBlock = (block, id) => {
    switch (block.name) {
      case "vendor_banner":
        return (
          <BannerBlock
            key={id}
            id={id}
            data={block.data}
            onUpdate={(newData) => updateBlock(id, newData)}
            onRemove={() => removeBlock(id)}
            bannerBlock={block.content || {}}
            bannerBlockImage={block.content?.right_content}
            onChangeBannerBlockField={(event, content) =>
              onChangeBannerBlockField(id, event, content)
            }
            onChangeBannerBlockDescription={(value) =>
              onChangeBannerBlockDescription(id, value)
            }
            onOpenBannerImageBlockSelector={(config) =>
              onOpenBannerImageBlockSelector(id, config)
            }
            onRemoveBannerBlockImage={() => onRemoveBannerBlockImage(id)}
            config={config}
          />
        );
      case "vendor_text_and_image":
        return (
          <TextAndImageBlock
            key={id}
            id={id}
            data={block.data}
            onUpdate={(newData) => updateBlock(id, newData)}
            onRemove={() => removeBlock(id)}
            textAndImageBlock={block.content || {}}
            textAndImageBlockImage={block.content?.right_content}
            onChangeTextAndImageBlockField={(event, content) =>
              onChangeTextAndImageBlockField(id, event, content)
            }
            onChangeTextAndImageBlockDescription={(value) =>
              onChangeTextAndImageBlockDescription(id, value)
            }
            onOpenTextAndImageBlockImageSelector={(config) =>
              onOpenTextAndImageBlockImageSelector(id, config)
            }
            onRemoveTextAndImageBlockImage={() =>
              onRemoveTextAndImageBlockImage(id)
            }
            config={config}
          />
        );
      case "vendor_image_and_text":
        return (
          <ImageAndTextBlock
            key={id}
            id={id}
            data={block.data}
            onUpdate={(newData) => updateBlock(id, newData)}
            onRemove={() => removeBlock(id)}
            imageAndTextBlock={block.content?.right_content || {}}
            imageAndTextBlockImage={block.content?.left_content}
            onChangeImageAndTextBlockField={(event, content) =>
              onChangeImageAndTextBlockField(id, event, content)
            }
            onChangeImageAndTextBlockDescription={(value) =>
              onChangeImageAndTextBlockDescription(id, value)
            }
            onOpenImageAndTextBlockImageSelector={(config) =>
              onOpenImageAndTextBlockImageSelector(id, config)
            }
            onRemoveImageAndTextBlockImage={() =>
              onRemoveImageAndTextBlockImage(id)
            }
            config={config}
          />
        );
      case "vendor_text_and_video":
        return (
          <TextAndVideoBlock
            key={id}
            id={id}
            data={block.data}
            onUpdate={(newData) => updateBlock(id, newData)}
            onRemove={() => removeBlock(id)}
            textAndVideoBlock={block.content || {}}
            onChangeTextAndVideoBlockField={(event, content) =>
              onChangeVendorTextAndVideoBlockField(id, event, content)
            }
            onChangeTextAndVideoBlockDescription={(value) =>
              onChangeVendorTextAndVideoBlockDescription(id, value)
            }
            config={config}
          />
        );
      case "vendor_video_and_text":
        return (
          <VideoAndTextBlock
            key={id}
            id={id}
            data={block.data}
            onUpdate={(newData) => updateBlock(id, newData)}
            onRemove={() => removeBlock(id)}
            videoAndTextBlock={block.content || {}}
            onChangeVideoAndTextBlockField={(event, content) =>
              onChangeVendorVideoAndTextBlockField(id, event, content)
            }
            onChangeVideoAndTextBlockDescription={(value) =>
              onChangeVendorVideoAndTextBlockDescription(id, value)
            }
            config={config}
          />
        );
      case "about":
        return (
          <ProcessBlock key={id} id={id} onRemove={() => removeBlock(id)} />
        );
      case "process":
        return (
          <ProcessBlock key={id} id={id} onRemove={() => removeBlock(id)} />
        );
      case "catalog":
        return (
          <ProductsBlock key={id} id={id} onRemove={() => removeBlock(id)} />
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="home-page">
        {/* <div className="card card-default"> */}
        {/* <div className="card-header card-header-border-bottom"> */}
        {/* <h2>Home Page Builder</h2> */}
        <div className="card-actions">
          <button
            className="btn btn-primary"
            onClick={savePageData}
            disabled={loading}
          >
            {loading ? "loading..." : "Save Page"}
          </button>
        </div>
        {/* </div> */}
        <div className="card-body">
          <div className="col-10">
            {/* Render existing blocks */}
            {blocks.map((block, index) => (
              <div
                key={index}
                className={`block-wrapper ${draggedIndex === index ? "dragging" : ""}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  cursor: "move",
                  display: "flex",
                }}
              >
                <div className="drag-handle">
                  <span className="mdi mdi-drag-vertical drag-icon"></span>
                </div>
                {renderBlock(block, index)}
              </div>
            ))}

            {/* Add Block Button */}
            <AddBlockButton
              onClick={() => setShowBlockOptions(!showBlockOptions)}
              showOptions={showBlockOptions}
              onBlockSelect={addBlock}
            />
          </div>
        </div>
        {/* </div> */}
      </div>
      {showImageSelector && (
        <ImageSelector
          Config={config}
          onClose={onClose}
          onSelectImageInfo={onSelectImages}
        />
      )}
      <div className="card-actions" style={{ marginTop: "1rem" }}>
        <button
          className="btn btn-primary"
          onClick={savePageData}
          disabled={loading}
        >
          {loading ? "loading..." : "Save Page"}
        </button>
      </div>
    </Layout>
  );
};

export default HomePage;
